import React, {
  FC,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useId,
  useState,
} from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FilterModel } from '../../../common/model/ui/filter.model';
import { FilterType } from '../../../common/model/ui/filter.type';
import {
  formatRangeFilterValue,
  getFilterLabel,
  getFilterOptionLabel,
  matchesFilterSearch,
  parseRangeFilterValue,
} from '../../../common/utils/services/list-filter-utils';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvListFilterEditorProps {
  filter: FilterModel;
  onApply: (value: string) => void;
  onCancel: () => void;
  footer?: ReactNode;
}

const RANGE_FILTER_STEP = 0.5;

const OvListFilterEditor: FC<OvListFilterEditorProps> = ({
  filter,
  onApply,
  onCancel,
  footer = null,
}) => {
  const { t } = useTranslation();
  const titleId = useId();
  const inputId = useId();
  const searchId = useId();
  const [draft, setDraft] = useState<string>(
    typeof filter.value === 'string' && filter.value
      ? filter.value
      : filter.defaultValue ?? ''
  );
  const [search, setSearch] = useState('');
  const label = getFilterLabel(filter, t);
  const options = (filter.options ?? []).filter(
    (option) => option.value !== ''
  );
  const matchingOptions = filter.search
    ? options.filter((option) => matchesFilterSearch(option, search, t))
    : options;
  const choices =
    filter.defaultValue === undefined
      ? [{ value: '', label: t('common.all') }, ...matchingOptions]
      : matchingOptions;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    onApply(draft.trim());
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCancel();
    }
  };

  const renderOptionsState = () => {
    const state = filter.optionsState;

    if (!state) {
      return null;
    }

    switch (state.status) {
      case 'pending':
        return <StateText role="status">{t(state.message)}</StateText>;

      case 'error':
        return (
          <StateText role="alert">
            {t(state.message)}
            <TextButton type="button" onClick={state.onRetry}>
              {t('common.actions.retry')}
            </TextButton>
          </StateText>
        );

      default: {
        const unhandledState: never = state;

        return unhandledState;
      }
    }
  };

  const renderField = () => {
    switch (filter.type) {
      case FilterType.text:
        return (
          <TextField>
            <label htmlFor={inputId}>
              {filter.fieldLabel ? t(filter.fieldLabel) : label}
            </label>
            <input
              id={inputId}
              type="text"
              autoComplete="off"
              autoFocus
              value={draft}
              placeholder={filter.placeholder ? t(filter.placeholder) : ''}
              onChange={(event) => setDraft(event.target.value)}
              onFocus={(event) => event.target.select()}
            />
          </TextField>
        );

      case FilterType.select:
      case FilterType.radio:
        return (
          <>
            {filter.search && (
              <SearchField>
                <label htmlFor={searchId}>{t(filter.search.label)}</label>
                <input
                  id={searchId}
                  type="search"
                  autoComplete="off"
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </SearchField>
            )}
            <Options>
              <VisuallyHiddenLegend>{label}</VisuallyHiddenLegend>
              {choices.map((option) => (
                <Option key={option.value}>
                  <input
                    type="radio"
                    name={filter.fieldName}
                    value={option.value}
                    checked={draft === option.value}
                    autoFocus={!filter.search && draft === option.value}
                    onChange={() => setDraft(option.value)}
                  />
                  <OptionText>
                    <span>
                      {option.value === ''
                        ? option.label
                        : getFilterOptionLabel(option, t)}
                    </span>
                    {option.secondaryLabel && (
                      <SecondaryLabel>{option.secondaryLabel}</SecondaryLabel>
                    )}
                    {option.note && <OptionNote>{option.note}</OptionNote>}
                  </OptionText>
                </Option>
              ))}
              {filter.search && search.trim() && !matchingOptions.length && (
                <StateText role="status">
                  {t(filter.search.emptyLabel)}
                </StateText>
              )}
            </Options>
            {renderOptionsState()}
          </>
        );

      case FilterType.slider:
        return (
          <SliderField>
            <Slider
              size="small"
              step={RANGE_FILTER_STEP}
              min={filter.min}
              max={filter.max}
              value={parseRangeFilterValue(draft, filter.min, filter.max)}
              valueLabelDisplay="on"
              getAriaLabel={() => label}
              onChange={(_, value) =>
                setDraft(
                  formatRangeFilterValue(
                    value as number[],
                    filter.min,
                    filter.max
                  )
                )
              }
            />
          </SliderField>
        );

      case FilterType.date_picker:
        return null;

      default: {
        const unhandledType: never = filter.type;

        return unhandledType;
      }
    }
  };

  return (
    <Panel
      $isTall={!!filter.search || !!footer}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onSubmit={onSubmit}
      onKeyDown={onKeyDown}
    >
      <Head>
        <h2 id={titleId}>{label}</h2>
        <IconButton
          type="button"
          aria-label={t('common.listFilters.close', { name: label })}
          onClick={onCancel}
        >
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </Head>
      <Body>{renderField()}</Body>
      {footer && <Footer>{footer}</Footer>}
      <Actions>
        <TextButton type="button" onClick={() => onApply('')}>
          {t('common.listFilters.clear')}
        </TextButton>
        <PrimaryButton type="submit">
          {t('common.listFilters.apply')}
        </PrimaryButton>
      </Actions>
    </Panel>
  );
};

export default OvListFilterEditor;

const Panel = styled.form<{ $isTall: boolean }>`
  display: flex;
  flex-direction: column;
  width: 20rem;
  max-width: calc(100vw - 2rem);
  max-height: min(
    ${({ $isTall }) => ($isTall ? '40rem' : '26rem')},
    calc(100vh - 2rem)
  );
  background-color: ${Colours.WHITE};
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  box-shadow: ${Variables.boxShadow.alternateBox};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: normal;
  text-align: left;
  white-space: normal;

  button,
  input {
    font: inherit;
    color: inherit;
  }

  button {
    cursor: pointer;
  }

  button:focus-visible,
  input:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const Head = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem 0.5625rem;

  h2 {
    margin: 0;
    font-size: ${Variables.fontSizes.LARGE};
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  min-height: 1.75rem;
  padding: 0.25rem;
  border: 0;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background: transparent;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const TextField = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 0.125rem 1rem 1rem;

  label {
    font-size: ${Variables.fontSizes.SMALL};
    color: ${Colours.OV_CHARCOAL};
  }

  input {
    width: 100%;
    min-height: 2.375rem;
    padding: 0.5rem 0.625rem;
    border: 1px solid ${Colours.OV_CHARCOAL};
    border-radius: ${Variables.borderRadius.SMALL};
    box-sizing: border-box;
  }
`;

const SliderField = styled.div`
  padding: 2rem 1.5rem 0.5rem;

  .MuiSlider-root {
    color: ${Colours.OV_BASE};
  }
`;

const SearchField = styled(TextField)`
  padding-bottom: 0.5rem;
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SecondaryLabel = styled.span`
  font-family: monospace;
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: normal;
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const OptionNote = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: normal;
  color: ${Colours.OV_CHARCOAL};
`;

const StateText = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.5rem 1rem;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const Footer = styled.div`
  display: flex;
  flex: none;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  padding: 0.5rem 1rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Options = styled.fieldset`
  display: grid;
  gap: 0.125rem;
  min-width: 0;
  margin: 0;
  padding: 0.125rem 0.5rem 0.5rem;
  border: 0;
`;

const VisuallyHiddenLegend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5625rem 0.625rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  cursor: pointer;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &:has(input:checked) {
    background-color: ${Colours.OV_LIGHT_BLUE};
    font-weight: bold;
  }

  input {
    flex: none;
    width: 1rem;
    height: 1rem;
    margin: 0;
    accent-color: ${Colours.OV_BASE};
  }
`;

const Actions = styled.div`
  display: flex;
  flex: none;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const TextButton = styled.button`
  min-height: 2.125rem;
  padding: 0.3125rem 0.4375rem;
  border: 1px solid transparent;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  background: transparent;

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }
`;

const PrimaryButton = styled.button`
  && {
    min-height: 2.125rem;
    padding: 0.375rem 0.8125rem;
    border: 1px solid ${Colours.OV_BASE};
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    background-color: ${Colours.OV_BASE};
    color: ${Colours.WHITE};
  }
`;
